import { FC, useState } from 'react';
import useAppointmentDetail from 'hooks/useAppointmentDetail';

import DefaultModal from 'components/Modals/DefaultModal';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as MailOutlineIcon } from 'assets/icons/mail_outline.svg';
import { ReactComponent as PhoneIphoneIcon } from 'assets/icons/phone_iphone.svg';
import { ReactComponent as BirthdayIcon } from 'assets/icons/cake.svg';
import { ReactComponent as ServiceIcon } from 'assets/icons/service.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-line.svg';
import { ReactComponent as PractitionerIcon } from 'assets/icons/practitioners-icon.svg';
import { ReactComponent as MarkerIcon } from 'assets/icons/google-location.svg';
import { ReactComponent as MedicalInformation } from 'assets/icons/medical-information.svg';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import {
  calculateAge,
  capitalizeAvatarName,
  concatFullName,
  filterOptionsByAcceptedOrCancelled,
  getErrorReason,
  getIsWarningIconEnabled,
  getOptionsForAcceptedStatus,
  getOptionsForNewStatus,
  getPostCodeOrZipTextBasedOnCountry,
  isWarningIconExistedAfter24Hour,
} from 'utils/common';
import moment from 'moment';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import CommonButton from 'components/CommonButton/CommonButton';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';
import { DATE_TIME_FORMAT, datetimeFormat } from 'utils/datetime';
import AppointmentDetailStatus from './AppointmentDetailStatus/AppointmentDetailStatus';
import {
  getAppointmentStartTime,
  getAppointmentEndTime,
  getHygienistWorkingTime,
  getDoctorWorkingTime,
} from 'components/TableColumn/utils/getAppointmentTime';
import { getIsDoctorStartBeforeHygienist } from 'pages/SchedulePage/TimeGrid/CardDetail/utils/getPractitionerDetail';
import { getDentistWorkingHour } from 'utils/getDentistWorkingHour';
import getPractitionerName from 'utils/getPractitionerName';
import useClinic from 'hooks/useClinic';
import useModal from 'hooks/useModal';
import { APPOINTMENT_STATUS, INSURANCE_PAY_FOR_MY_SELF } from 'utils/constants';
import RejectAppointmentModal from 'components/RejectAppointmentModal/RejectAppointmentModal';
import { AxiosRequestConfig } from 'axios';

interface AppointmentDetailModalProps {
  appointmentId: string;
  isHiddenAcceptRejectOptions?: boolean;
  onClose: () => void;
  axiosConfig?: AxiosRequestConfig<any>;
}

const initialModalState = { isSubmitting: false, type: '' };

const AppointmentDetailModal: FC<AppointmentDetailModalProps> = ({
  appointmentId,
  isHiddenAcceptRejectOptions,
  onClose,
  axiosConfig,
}) => {
  const { data: clinic, isLoading: isClinicLoading } = useClinic(axiosConfig);
  const { data, isLoading: isAppointmentDetailLoading } = useAppointmentDetail(
    appointmentId,
    !!clinic,
    axiosConfig
  );

  const isLoading = isClinicLoading || isAppointmentDetailLoading;

  const [modalState, setModalState] = useState(initialModalState);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { isAllowed, setIsAllowed } = useModal({ onClose, isLoading });

  const matchMutate = useMatchMutate();

  if (isLoading) {
    return <LoadingScreen />;
  }

  const errorReason = getErrorReason(data);

  const isAppointmentInThePast = moment(
    data.appointmentDate,
    'YYYY-MM-DD'
  ).isBefore(moment.tz(clinic?.timezone).format('YYYY-MM-DD'), 'day');

  const getOptions = () => {
    const isWarningIconEnabled = getIsWarningIconEnabled(data);
    const isExistedAfter24Hour = isWarningIconExistedAfter24Hour(data);

    // Props take highest priority
    if (isHiddenAcceptRejectOptions) {
      return {
        isAppointmentAcceptable: false,
        isAppointmentRejectable: false,
      };
    }

    if (
      isWarningIconEnabled &&
      data.status === APPOINTMENT_STATUS.ACCEPTED.KEY
    ) {
      return {
        isAppointmentAcceptable: false,
        isAppointmentRejectable: false,
      };
    }

    if (isWarningIconEnabled && isExistedAfter24Hour) {
      return {
        isAppointmentAcceptable: true,
        isAppointmentRejectable: true,
      };
    }

    let options = [];

    if (isWarningIconEnabled || (data.isAccepted && errorReason === null)) {
      return {
        isAppointmentAcceptable: false,
        isAppointmentRejectable: false,
      };
    }

    if (data.isAccepted) {
      options = getOptionsForAcceptedStatus({
        errorReason,
      });
    } else {
      options = getOptionsForNewStatus({
        currentStatus: data.status,
        errorReason,
        isAppointmentInThePast,
      });
    }

    options = filterOptionsByAcceptedOrCancelled({
      options,
      appointmentDetails: data,
    });

    const isAppointmentAcceptable = options.includes('Accept');
    const isAppointmentRejectable = options.includes('Reject');

    return {
      isAppointmentAcceptable,
      isAppointmentRejectable,
    };
  };

  const { isAppointmentAcceptable, isAppointmentRejectable } = getOptions();

  const onSubmit = async (status: 'accept' | 'reject') => {
    setIsAllowed(false);
    setModalState({ isSubmitting: true, type: status });

    const updatedStatus = status === 'accept' ? 'accepted' : 'cancelled';
    const toastMessage = status === 'accept' ? 'accepted' : 'rejected';

    const payload = [
      {
        appointmentId: data.id,
        status: updatedStatus,
      },
    ];
    try {
      await axiosInstance.patch('/appointments', payload);
      await matchMutate(/\/appointments\?[\s\S]+/);
      renderToast({
        type: 'success',
        message: `Successfully ${toastMessage} appointment`,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
    setIsAllowed(true);
    setModalState(initialModalState);
  };

  const onCloseModal = () => {
    if (isAllowed) {
      onClose();
    }
  };

  const renderDentistHygienistInfo = () => {
    if (data.linkedAppointment) {
      return (
        <div className="flex gap-x-1">
          <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <div
            className={`flex flex-col ${
              getIsDoctorStartBeforeHygienist(data) ? '' : 'flex-col-reverse'
            } `}
          >
            <div className="flex gap-x-0.5">
              <span>Doctor: </span>
              <span>
                {getPractitionerName(data.linkedAppointment.doctor)} (
                {getDoctorWorkingTime(data)})
              </span>
            </div>
            <div className="flex gap-x-0.5">
              <span>Hygienist: </span>
              <span>
                {getPractitionerName(data.doctor)} (
                {getHygienistWorkingTime(data)})
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (data.isSplitScheduling) {
      return (
        <div className="flex gap-x-1">
          <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <div className="flex flex-col-reverse">
            <div className="flex gap-x-0.5">
              <span>Doctor: </span>
              <span>{getDentistWorkingHour(data)}</span>
            </div>
            <div className="flex gap-x-0.5">
              <span>Hygienist: </span>
              <span>
                {getPractitionerName(data.doctor)} (
                {getHygienistWorkingTime(data)})
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex gap-x-1 items-center">
        <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
        <span>{getPractitionerName(data.doctor)}</span>
      </div>
    );
  };

  const renderInsuranceInfo = (insuranceCoverage: {
    id: string;
    insuranceCarrierName: string;
    memberId: string;
    groupNumberId: string;
    subscriberDob: string;
    subscriberFullName: string;
    employerName: string;
    relationToSubscriber: string;
    insuranceFront: string | null;
    insuranceBack: string | null;
  }) => {
    const excludeFields = ['id', 'insuranceCarrierName'];

    const isInsuranceInfo =
      insuranceCoverage.insuranceFront || insuranceCoverage.insuranceBack;

    const fields = [
      { key: 'memberId', label: 'Member ID' },
      { key: 'groupNumberId', label: 'Group ID' },
      { key: 'subscriberFullName', label: `Subscriber's Full Name` },
      { key: 'subscriberDob', label: `Subscriber's DOB` },
      { key: 'relationToSubscriber', label: `Relation to Subscriber` },
      { key: 'employerName', label: `Employer Name` },
    ];

    if (isInsuranceInfo) {
      return (
        <div className="flex gap-x-1.2 min-h-[9rem]">
          {insuranceCoverage.insuranceFront && (
            <div className="w-16">
              <a
                href={insuranceCoverage.insuranceFront}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={insuranceCoverage.insuranceFront}
                  alt="Insurance Front"
                  className="max-w-full h-auto rounded-[0.8rem] border border-light-grey aspect-[16/9]"
                />
              </a>
            </div>
          )}
          {insuranceCoverage.insuranceBack && (
            <div className="w-16">
              <a
                href={insuranceCoverage.insuranceBack}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={insuranceCoverage.insuranceBack}
                  alt="Insurance Back"
                  className="max-w-full h-auto rounded-[0.8rem] border border-light-grey aspect-[16/9]"
                />
              </a>
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="flex flex-row gap-x-1 items-center mb-1.2">
          <MedicalInformation className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <span>{insuranceCoverage.insuranceCarrierName}</span>
        </div>

        <div className="grid grid-cols-2 gap-x-1.6 gap-y-1.2 rounded-[0.8rem] border border-light-grey py-1.2 px-1.6 bg-[#FCFCFC]">
          {fields.map(({ key, label }: { key: string; label: string }) => {
            if (excludeFields.includes(key)) {
              return null;
            }

            let value = '-';

            if ((insuranceCoverage as any)[key]) {
              if (key === 'subscriberDob') {
                value = datetimeFormat({
                  dateString: insuranceCoverage[key],
                  pattern: DATE_TIME_FORMAT.DOB,
                });
              } else {
                value = (insuranceCoverage as any)[key];
              }
            }

            return (
              <div key={key} className="flex gap-x-0.5">
                <span className="font-bold text-12 min-w-[150px]">
                  {label}:{' '}
                </span>
                <span className="text-14 words-break">{value}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (isCancelModalOpen) {
    return (
      <RejectAppointmentModal
        appointmentId={appointmentId}
        onClose={() => {
          setIsCancelModalOpen(false);
          setIsAllowed(true);
        }}
        onDone={() => {
          onClose();
        }}
      />
    );
  }

  const renderProcedures = () => {
    const syncedPmsProcedures = data.syncedPmsProcedures || [];

    if (syncedPmsProcedures.length) {
      const procedureCodes = syncedPmsProcedures.map((item: any) => (
        <div>{`${item.code} - ${item.name}`}</div>
      ));

      return (
        <div className="mt-1.2 words-break whitespace-pre-line">
          {procedureCodes}
        </div>
      );
    }

    return '-';
  };

  const renderDescriptors = () => {
    const syncedPmsDescriptors = data.syncedPmsDescriptors || [];

    if (syncedPmsDescriptors.length) {
      const descriptors = syncedPmsDescriptors.map((item: any) => (
        <div>{`${item.code} - ${item.name} - ${item.laymanDescription}`}</div>
      ));

      return (
        <div className="mt-1.2 words-break whitespace-pre-line">
          {descriptors}
        </div>
      );
    }

    return '-';
  };

  return (
    <DefaultModal>
      <div className="rounded-[1.6rem] shadow-elevation-07 min-w-[70rem] max-h-[90vh] p-3 text-darkest-grey text-14 leading-[2.1rem] overflow-y-auto scrollbar">
        <section className="flex justify-between">
          <AppointmentDetailStatus appointment={data} />
          <button onClick={onCloseModal}>
            <CloseIcon />
          </button>
        </section>

        <section className="flex mt-1.6 gap-x-2.4">
          <div className="w-6 h-6 bg-magenta rounded-full flex items-center justify-center text-white text-16 shrink-0">
            {data.patient &&
              capitalizeAvatarName(
                concatFullName({
                  firstName: data.patient.firstName,
                  lastName: data.patient.lastName,
                })
              )}
          </div>
          <div className="flex flex-col pt-1.2">
            <span className="text-18 leading-[2.7rem] font-bold">
              {data.patient
                ? `${data.patient.name}${
                    data.patient.dob
                      ? `, ${calculateAge(data.patient.dob, 'MMM DD, YYYY')}`
                      : ''
                  }`
                : '-'}
            </span>
            <span className="text-12 leading-[1.8rem]">
              Requested on:{' '}
              {moment(data.createdAt).format('MMM DD, YYYY hh:mm A')}
            </span>
          </div>
        </section>

        <main className="mt-3 flex flex-col gap-y-2 pb-3">
          <section className="grid grid-cols-2 gap-x-1.6 gap-y-1">
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Patient Info
            </h3>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Booking Details
            </h3>
            <div className="flex gap-x-1 items-center mt-0.2">
              <MailOutlineIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
              <span>{data.patient?.email || '-'}</span>
            </div>
            <div className="flex gap-x-1 items-center mt-0.2">
              <ServiceIcon className="w-2 h-2 all-child:fill-mid-grey" />
              <span>{data.service?.name}</span>
            </div>

            <div className="flex gap-x-1 items-center">
              <PhoneIphoneIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
              <span>
                {data.patient
                  ? formatFullPhoneNumber(`+${data.patient.phoneNumber}`)
                  : '-'}
              </span>
            </div>
            <div className="flex gap-x-1 items-center">
              <TimeIcon className="w-2 h-2 all-child:fill-mid-grey" />
              <span>
                {datetimeFormat({
                  dateString: data.appointmentDate,
                  pattern: 'MMM DD, YYYY',
                })}{' '}
                {getAppointmentStartTime(data)}
                {' - '}
                {getAppointmentEndTime(data)}
              </span>
            </div>

            <div className="flex flex-col gap-y-1">
              <div className="flex gap-x-1">
                <BirthdayIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>
                  {data.patient
                    ? datetimeFormat({
                        dateString: data.patient.dob,
                        pattern: DATE_TIME_FORMAT.DOB,
                      })
                    : '-'}
                </span>
              </div>
              {data.patient?.homeAddress && (
                <div className="flex gap-x-1 items-center">
                  <MarkerIcon className="w-2 h-2 all-child:fill-mid-grey shrink-0 self-start" />
                  <span className="inline-block max-w-[80%]">
                    {data.patient.homeAddress}
                    {data.patient.postalCode
                      ? ` - ${getPostCodeOrZipTextBasedOnCountry(
                          clinic.location?.country
                        )}: ${data.patient.postalCode}`
                      : ''}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-y-1">
              {renderDentistHygienistInfo()}
              {data.externalId && (
                <div className="flex gap-x-1 col-start-2">
                  {data.isSplitScheduling ? (
                    <span>Hygienist Appt ID:</span>
                  ) : (
                    <span>Appt ID:</span>
                  )}
                  <span>{data.externalId}</span>
                </div>
              )}
              {data.linkedAppointment && data.linkedAppointment.externalId && (
                <div className="flex gap-x-1 col-start-2">
                  <span>Doctor Appt ID:</span>
                  <span>{data.linkedAppointment.externalId}</span>
                </div>
              )}
            </div>
          </section>

          <section className="grid grid-cols-1 gap-x-1.6 gap-y-1.2">
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Insurance
            </h3>
            <div>
              {data.insuranceCoverage ? (
                renderInsuranceInfo(data.insuranceCoverage)
              ) : (
                <span>
                  {data.insuranceName === INSURANCE_PAY_FOR_MY_SELF
                    ? 'Patient self-pay'
                    : '-'}
                </span>
              )}
            </div>
          </section>

          <section className="grid grid-cols-2 gap-x-1.6 gap-y-1.2">
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Last appointment
            </h3>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Next appointment
            </h3>
            <span>
              {data.lastPastAppointment
                ? `${datetimeFormat({
                    dateString: data.lastPastAppointment.appointmentDate,
                    pattern: 'MMM DD, YYYY',
                  })} - ${getPractitionerName(data.lastPastAppointment.doctor)}`
                : '-'}
            </span>
            <span>
              {data.nextFutureAppointment
                ? `${datetimeFormat({
                    dateString: data.nextFutureAppointment.appointmentDate,
                    pattern: 'MMM DD, YYYY',
                  })} - ${getPractitionerName(
                    data.nextFutureAppointment.doctor
                  )}`
                : '-'}
            </span>
          </section>

          <section>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Procedures
            </h3>
            {renderProcedures()}
          </section>

          <section>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Descriptors
            </h3>
            {renderDescriptors()}
          </section>

          <section>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              Patient Notes
            </h3>
            {data.patientNote ? (
              <p className="mt-1.2 words-break whitespace-pre-line">
                {data.patientNote}
              </p>
            ) : (
              '-'
            )}
          </section>

          <section>
            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
              PMS Notes
            </h3>
            {data.pmsNote ? (
              <p className="mt-1.2 whitespace-pre-line">{data.pmsNote}</p>
            ) : (
              '-'
            )}
          </section>
        </main>

        <div className="flex justify-end gap-x-1.6">
          {!isAppointmentInThePast && isAppointmentRejectable && (
            <CommonButton
              variant="secondary"
              isLoading={
                modalState.isSubmitting && modalState.type === 'reject'
              }
              disabled={modalState.isSubmitting && modalState.type === 'accept'}
              onClick={() => {
                setIsAllowed(false);
                setIsCancelModalOpen(true);
              }}
            >
              Reject
            </CommonButton>
          )}
          {isAppointmentAcceptable && (
            <CommonButton
              onClick={() => onSubmit('accept')}
              isLoading={
                modalState.isSubmitting && modalState.type === 'accept'
              }
              disabled={modalState.isSubmitting && modalState.type === 'reject'}
            >
              Accept
            </CommonButton>
          )}
        </div>
      </div>
    </DefaultModal>
  );
};

export default AppointmentDetailModal;
